import React from "react";
import MUIDialog from "@mui/material/Dialog";
import MUIDialogTitle from "@mui/material/DialogTitle";
import MUIDialogContent from "@mui/material/DialogContent";
import MUIIconButton from "@mui/material/IconButton";
import MUICloseIcon from "@mui/icons-material/Close";
import MUITooltip from "@mui/material/Tooltip";

/**
 * Renders the dialog header. Defaults to empty; override via props if needed.
 * @returns {React.ReactNode|null}
 */
function defaultRenderHeader() {
  return null;
}

/**
 * Renders the dialog content. Defaults to empty; override via props if needed.
 * @returns {React.ReactNode|null}
 */
function defaultRenderContent() {
  return null;
}

/**
 * Renders the dialog actions. Defaults to empty; override via props if needed.
 * @returns {React.ReactNode|null}
 */
function defaultRenderActions() {
  return null;
}

/**
 * Returns the max width of the dialog.
 * @returns {string} One of xs, sm, md, lg, xl.
 */
function defaultMaxWidth() {
  return "md";
}

/**
 * Returns whether the dialog should be full width.
 * @returns {boolean}
 */
function defaultFullWidth() {
  return true;
}

/**
 * Returns whether to show dividers on the content area.
 * @returns {boolean}
 */
function defaultDividers() {
  return false;
}

/**
 * Returns whether to blur the background. Use sparingly; only needed in
 * specific cases.
 * @returns {boolean}
 */
function defaultBlur() {
  return true;
}

/**
 * Returns sx props for the dialog content area.
 * @returns {object}
 */
function defaultContentSx() {
  return {};
}

/**
 * Returns whether the dialog is open by default.
 * @returns {boolean}
 */
function defaultOpen() {
  return true;
}

/**
 * Returns whether to disable closing the dialog with the escape key.
 * @returns {boolean}
 */
function defaultDisableEscapeKeyDown() {
  return false;
}

/**
 * Returns the default onClose handler.
 * @returns {function}
 */
function defaultOnClose() {
  return null;
}

/**
 * DialogFunctional component.
 * Renders a Material UI Dialog with customizable header, content, actions, and options.
 * @param {object} props
 * @param {boolean} [props.open]
 * @param {boolean} [props.disableEscapeKeyDown]
 * @param {function} [props.onClose]
 * @param {function} [props.renderHeader]
 * @param {function} [props.renderContent]
 * @param {function} [props.renderActions]
 * @param {function} [props.getMaxWidth]
 * @param {function} [props.getFullWidth]
 * @param {function} [props.getDividers]
 * @param {function} [props.getBlur]
 * @param {function} [props.getContentSx]
 * @returns {React.ReactElement}
 */
export default function DialogFunctional({
  open = defaultOpen(),
  disableEscapeKeyDown = defaultDisableEscapeKeyDown(),
  onClose = defaultOnClose(),
  renderHeader = defaultRenderHeader,
  renderContent = defaultRenderContent,
  renderActions = defaultRenderActions,
  getMaxWidth = defaultMaxWidth,
  getFullWidth = defaultFullWidth,
  getDividers = defaultDividers,
  getBlur = defaultBlur,
  getContentSx = defaultContentSx,
}) {
  /**
   * Renders the MUI Dialog component.
   * @returns {React.ReactElement}
   */
  const render = () => {
    return (
      <MUIDialog
        sx={{ backdropFilter: `blur(${getBlur() ? "3px" : "0px"})` }}
        open={open || false}
        fullWidth={getFullWidth()}
        maxWidth={getMaxWidth()}
        onClose={(event, reason) => {
          if (reason === "escapeKeyDown" && disableEscapeKeyDown) {
            return;
          }
          onClose();
        }}
      >
        <MUIDialogTitle component="div">
          {renderHeader()}
          {renderCloseIcon()}
        </MUIDialogTitle>
        <MUIDialogContent dividers={getDividers()} sx={getContentSx()}>
          {renderContent()}
        </MUIDialogContent>
        {renderActions()}
      </MUIDialog>
    );
  };

  /**
   * Renders the dialog close icon button.
   * @returns {React.ReactElement}
   */
  function renderCloseIcon() {
    return (
      <MUITooltip title="Close" placement="left">
        <MUIIconButton
          onClick={() => onClose()}
          sx={{
            position: "absolute",
            right: 16,
            top: 14,
          }}
        >
          <MUICloseIcon />
        </MUIIconButton>
      </MUITooltip>
    );
  }

  return render();
}
